import React, { useEffect, useState } from "react";
import { Row, Col, Badge, Card, Button, Spinner, Tabs, Tab, ListGroup } from "react-bootstrap";
import { getDownloadLink, getAudioLink } from "../api/appApi";
import { ImFileVideo } from "react-icons/im";
import { FaFileAudio, FaFileImage, FaDownload, FaHeadphones } from "react-icons/fa";
import { useLocation } from "react-router-dom";
import queryString from "query-string";

const formatBytes = (bytes, decimals = 2) => {
    if (!+bytes) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

const extractVideoId = (input) => {
    const s = String(input || '').trim();
    const patterns = [
        /(?:youtu\.be\/|watch\?v=|embed\/|shorts\/|v\/)([A-Za-z0-9_-]{11})/,
        /^([A-Za-z0-9_-]{11})$/
    ];
    for (const pattern of patterns) {
        const match = s.match(pattern);
        if (match) return match[1];
    }
    return null;
};

const DownloadContainer = ({ data, handleSearch }) => {
  const location = useLocation();
  const search = queryString.parse(location?.search);
  const videoId = extractVideoId(search?.query);
  const [downloadingIndex, setDownloadingIndex] = useState(null);
  const [activeTab, setActiveTab] = useState('video');

  const videoRef = React.useRef(null);
  const [videoHeight, setVideoHeight] = useState('auto');
  // bgMode: false | 'quick' (giu iframe, co tieng ngay) | 'audio' (the <audio> stream, tat man hinh + toc do)
  const [bgMode, setBgMode] = useState(false);
  const audioRef = React.useRef(null);
  const [rate, setRate] = useState(1);
  const audioUrl = videoId ? getAudioLink(videoId) : null;
  const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
  // FIX pause->play restart tu dau: luu vi tri, restore khi metadata/error/stall.
  const resumePosRef = React.useRef(0);
  const retryCountRef = React.useRef(0);
  const audioPosKey = videoId ? `yt-audio-pos:${videoId}` : null;

  const saveAudioPos = (t) => {
    if (!audioPosKey || !isFinite(t) || t < 1) return;
    resumePosRef.current = t;
    try { localStorage.setItem(audioPosKey, String(Math.floor(t))); } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (!bgMode || !data?.title || !('mediaSession' in navigator)) return;
    const artwork = Array.isArray(data.thumbnails)
      ? data.thumbnails.map(t => ({ src: t?.url, sizes: t?.width ? `${t.width}x${t.height}` : '' }))
      : data.thumbnails?.url ? [{ src: data.thumbnails.url }] : [];
    try {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: data.title,
        artist: data.Video?.author || '',
        artwork
      });
    } catch (e) { /* ignore */ }
  }, [bgMode, data]);

  // UI-only background (quick): keep the already-loaded YouTube iframe alive,
  // just hide it + block visibilitychange so YT doesn't auto-pause.
  useEffect(() => {
    if (bgMode !== 'quick') return;
    const block = (e) => { e.stopImmediatePropagation(); };
    document.addEventListener('visibilitychange', block, true);
    let origHiddenDesc = null;
    try {
      origHiddenDesc = Object.getOwnPropertyDescriptor(Document.prototype, 'hidden');
      Object.defineProperty(document, 'hidden', { value: false, configurable: true });
    } catch (e) { /* ignore */ }
    return () => {
      document.removeEventListener('visibilitychange', block, true);
      try {
        if (origHiddenDesc) {
          Object.defineProperty(Document.prototype, 'hidden', origHiddenDesc);
        } else {
          delete document.hidden;
        }
      } catch (e) { /* ignore */ }
    };
  }, [bgMode]);

  const startBackgroundQuick = () => {
    setBgMode('quick');
    window.scrollTo(0, 0);
  };

  const startBackgroundAudio = () => {
    setBgMode('audio');
    window.scrollTo(0, 0);
  };

  const stopBackground = () => setBgMode(false);

  // Toc do phat cho the <audio> + gan MediaSession handlers de khoa man hinh van dieu khien duoc
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.playbackRate = rate;
  }, [rate, bgMode]);

  // Restore vi tri da luu khi doi video / vao che do audio (fix play lai tu dau)
  useEffect(() => {
    retryCountRef.current = 0;
    if (bgMode !== 'audio' || !videoId) return;
    try {
      const saved = parseInt(localStorage.getItem(`yt-audio-pos:${videoId}`) || '0', 10);
      resumePosRef.current = isFinite(saved) && saved > 5 ? saved : 0;
    } catch (e) { resumePosRef.current = 0; }
  }, [bgMode, videoId]);

  useEffect(() => {
    if (bgMode !== 'audio' || !('mediaSession' in navigator)) return;
    try {
      navigator.mediaSession.setActionHandler('play', () => audioRef.current?.play());
      navigator.mediaSession.setActionHandler('pause', () => audioRef.current?.pause());
      navigator.mediaSession.setActionHandler('seekbackward', () => {
        if (audioRef.current) audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 10);
      });
      navigator.mediaSession.setActionHandler('seekforward', () => {
        if (audioRef.current) audioRef.current.currentTime += 30;
      });
    } catch (e) { /* ignore */ }
    return () => {
      try {
        navigator.mediaSession.setActionHandler('play', null);
        navigator.mediaSession.setActionHandler('pause', null);
        navigator.mediaSession.setActionHandler('seekbackward', null);
        navigator.mediaSession.setActionHandler('seekforward', null);
      } catch (e) { /* ignore */ }
    };
  }, [bgMode]);

  useEffect(() => {
    if (location.search) handleSearch(search?.query);
    window.scrollTo(0, 0);
  }, []);

  // Update height when video resizes or loads
  useEffect(() => {
    const updateHeight = () => {
        if (window.innerWidth >= 992 && videoRef.current) {
            setVideoHeight(videoRef.current.offsetHeight);
        } else {
            setVideoHeight('auto');
        }
    };

    // Initial check
    updateHeight();

    // Listener for resize
    window.addEventListener('resize', updateHeight);

    // Check periodically for a short time in case of dynamic loading
    const interval = setInterval(updateHeight, 500);
    setTimeout(() => clearInterval(interval), 3000);

    return () => {
        window.removeEventListener('resize', updateHeight);
        clearInterval(interval);
    }
  }, [data]);

  const handleDownload = (url, index) => {
    setDownloadingIndex(index);
    window.location.href = url;

    setTimeout(() => {
      setDownloadingIndex(null);
    }, 2000);
  };

  // Helper to filter and render list
  const renderDownloadList = (items, type) => {
    if (!items || items.length === 0) return <p className="text-muted text-center py-3">No formats available.</p>;

    return (
      <div className="custom-scroll flex-grow-1" style={{ overflowY: 'auto', overflowX: 'hidden' }}>
          <ListGroup variant="flush">
            {items.map((item, localIndex) => {
                // Re-calculate original index from data.formats to ensure correct download link
            const originalIndex = data.formats.indexOf(item);

            let icon = <ImFileVideo size={24} className="text-danger" />;
            if (type === 'no-audio') icon = <FaFileImage size={24} className="text-warning" />;
            if (type === 'audio') icon = <FaFileAudio size={24} className="text-info" />;

            const fileExtension = item?.mimeType ? item.mimeType.split(';')[0].split('/')[1] : "unk";
            let qualityText = item?.qualityLabel || item?.audioQuality || "N/A";

            // Format quality text for Audio
            if (qualityText.includes("AUDIO_QUALITY_")) {
                qualityText = qualityText.replace("AUDIO_QUALITY_", "").toLowerCase();
                qualityText = qualityText.charAt(0).toUpperCase() + qualityText.slice(1);
            }

            const sizeText = item?.contentLength ? formatBytes(item.contentLength) : "N/A";

            return (
              <ListGroup.Item key={originalIndex} className="d-flex align-items-center justify-content-between py-3 border-bottom ps-4" style={{ paddingRight: '10px' }}>
                  <div className="d-flex align-items-center gap-3">
                      <div className="bg-light rounded-circle d-flex align-items-center justify-content-center" style={{width: '40px', height: '40px'}}>
                        {icon}
                      </div>
                      <div>
                          <div className="fw-bold text-dark">{qualityText} <Badge bg="secondary" className="ms-1 text-uppercase" style={{fontSize: '0.7rem'}}>{fileExtension}</Badge></div>
                          <div className="small text-muted">{sizeText}</div>
                      </div>
                  </div>

                  <Button
                      variant={downloadingIndex === originalIndex ? "secondary" : "danger"}
                      className="rounded-3 px-3 fw-semibold"
                      onClick={() => handleDownload(getDownloadLink(search.query, originalIndex), originalIndex)}
                      disabled={downloadingIndex === originalIndex}
                  >
                      {downloadingIndex === originalIndex ? (
                          <Spinner size="sm" animation="border" />
                      ) : <FaDownload />}
                      <span className="ms-2 d-none d-sm-inline">{downloadingIndex === originalIndex ? "Preparing..." : "Download"}</span>
                  </Button>
              </ListGroup.Item>
            );
        })}
      </ListGroup>
    </div>
    );
  };

  // Filter data
  const videoFormats = data?.formats?.filter(item => item.qualityLabel && item.audioQuality);
  const videoNoAudioFormats = data?.formats?.filter(item => item.qualityLabel && !item.audioQuality);
  const audioFormats = data?.formats?.filter(item => !item.qualityLabel && item.audioQuality);

  return (
    <div className="download-container">
        <Row className="g-4">
            <Col lg={8}>
                 <Card ref={videoRef} className="border-0 shadow-sm overflow-hidden mb-4">
                    <div className="ratio ratio-16x9 bg-dark" style={{ position: 'relative' }}>
                        {videoId && bgMode !== 'audio' && (
                            <iframe
                                src={`https://www.youtube-nocookie.com/embed/${videoId}?rel=0&autoplay=${bgMode === 'quick' ? 1 : 0}`}
                                title="YouTube Video Player"
                                className="w-100 h-100"
                                style={bgMode === 'quick'
                                  ? { position: 'absolute', width: '1px', height: '1px', opacity: 0, pointerEvents: 'none', border: 0 }
                                  : { border: 0 }}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowFullScreen
                            />
                        )}
                        {!videoId && data?.thumbnails && (
                            <img
                                src={Array.isArray(data.thumbnails) ? data.thumbnails.slice(-1)[0]?.url : data.thumbnails.url}
                                alt="video thumbnail"
                                className="w-100 h-100 object-fit-cover"
                            />
                        )}
                        {bgMode && videoId && (
                            <div className="w-100 h-100 d-flex align-items-center justify-content-center text-light">
                                <div className="text-center px-3">
                                    <FaHeadphones size={40} className="mb-2" />
                                    {bgMode === 'quick' && (
                                      <p className="mb-0">Đang phát nền nhanh — âm thanh giữ nguyên từ video đã tải</p>
                                    )}
                                    {bgMode === 'audio' && audioUrl && (
                                      <div>
                                        <p className="mb-2">Nghe nền — tắt màn hình vẫn chạy, chỉnh tốc độ bên dưới</p>
                                        <audio
                                            key={videoId}
                                            ref={audioRef}
                                            src={audioUrl}
                                            controls
                                            autoPlay
                                            preload="auto"
                                            className="w-100"
                                            style={{ maxWidth: '420px' }}
                                            onLoadedMetadata={(e) => {
                                              const a = e.currentTarget;
                                              a.playbackRate = rate;
                                              const want = resumePosRef.current;
                                              if (want > 5 && isFinite(a.duration) && want < a.duration - 5) {
                                                try { a.currentTime = want; } catch (err) { /* ignore */ }
                                              }
                                            }}
                                            onTimeUpdate={(e) => saveAudioPos(e.currentTarget.currentTime)}
                                            onPause={(e) => saveAudioPos(e.currentTarget.currentTime)}
                                            onPlay={(e) => {
                                              // Neu browser reset ve 0 sau stall, tra lai vi tri cu
                                              const a = e.currentTarget;
                                              const want = resumePosRef.current;
                                              if (want > 5 && a.currentTime < 1) {
                                                try { a.currentTime = want; } catch (err) { /* ignore */ }
                                              }
                                            }}
                                            onWaiting={(e) => saveAudioPos(e.currentTarget.currentTime)}
                                            onStalled={(e) => saveAudioPos(e.currentTarget.currentTime)}
                                            onError={(e) => {
                                              const a = e.currentTarget;
                                              const want = resumePosRef.current || a.currentTime;
                                              if (want > 5 && retryCountRef.current < 3) {
                                                retryCountRef.current += 1;
                                                // Giu nguyen src (backend da cache URL), chi seek lai + play
                                                setTimeout(() => {
                                                  try {
                                                    a.currentTime = want;
                                                    a.play().catch(() => {});
                                                  } catch (err) { /* ignore */ }
                                                }, 800 * retryCountRef.current);
                                              } else {
                                                retryCountRef.current = 0;
                                              }
                                            }}
                                        />
                                        <div className="d-flex flex-wrap justify-content-center gap-1 mt-2">
                                            {SPEEDS.map(s => (
                                                <Button
                                                    key={s}
                                                    size="sm"
                                                    variant={rate === s ? "warning" : "outline-light"}
                                                    onClick={() => setRate(s)}
                                                >
                                                    {s}x
                                                </Button>
                                            ))}
                                        </div>
                                        <p className="small text-muted mt-1 mb-0">Đang phát {rate}x</p>
                                      </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    <Card.Body>
                        <h4 className="fw-bold text-dark mb-2">{data?.title || "Loading..."}</h4>
                        {videoId && !bgMode && (
                            <div className="d-flex flex-wrap gap-2">
                                <Button
                                    variant="success"
                                    className="rounded-3 px-3 fw-semibold"
                                    onClick={startBackgroundQuick}
                                >
                                    <FaHeadphones className="me-2" />
                                    Phát nền nhanh (có tiếng ngay)
                                </Button>
                                <Button
                                    variant="primary"
                                    className="rounded-3 px-3 fw-semibold"
                                    onClick={startBackgroundAudio}
                                >
                                    <FaHeadphones className="me-2" />
                                    Nghe nền (tắt màn hình + tốc độ)
                                </Button>
                            </div>
                        )}
                        {bgMode && (
                            <div className="d-flex flex-wrap gap-2">
                                <Button
                                    variant="secondary"
                                    className="rounded-3 px-3 fw-semibold"
                                    onClick={stopBackground}
                                >
                                    Quay lại xem online
                                </Button>
                                {bgMode === 'quick' && (
                                    <Button
                                        variant="outline-primary"
                                        className="rounded-3 px-3 fw-semibold"
                                        onClick={startBackgroundAudio}
                                    >
                                        Chuyển sang nghe nền (tốc độ)
                                    </Button>
                                )}
                                {bgMode === 'audio' && (
                                    <Button
                                        variant="outline-success"
                                        className="rounded-3 px-3 fw-semibold"
                                        onClick={startBackgroundQuick}
                                    >
                                        Chuyển sang phát nhanh
                                    </Button>
                                )}
                            </div>
                        )}
                        {bgMode === 'quick' && (
                            <p className="small text-muted mt-2 mb-0">
                                Phát nền nhanh: giữ nguyên player YouTube đã load, video 45h cũng có tiếng ngay.
                                Lưu ý: tắt màn hình điện thoại có thể bị pause — hãy dùng chế độ nghe nền.
                            </p>
                        )}
                        {bgMode === 'audio' && (
                            <p className="small text-muted mt-2 mb-0">
                                Nghe nền: stream audio trực tiếp từ server (seek + tốc độ + tắt màn hình).
                                Lần đầu chờ vài giây để lấy link, sau đó tua/chỉnh tốc độ thoải mái.
                            </p>
                        )}
                    </Card.Body>
                </Card>
            </Col>

            <Col lg={4}>
                 <Card className="border-0 shadow-sm d-flex flex-column" style={{ height: videoHeight }}>
                    <Card.Header className="bg-white border-bottom-0 pt-4 px-4 flex-shrink-0">
                        <h5 className="fw-bold mb-0">Download Options</h5>
                    </Card.Header>
                    <Card.Body className="p-0 d-flex flex-column flex-grow-1 overflow-hidden">
                        {data?.formats ? (
                            <Tabs
                                id="download-tabs"
                                activeKey={activeTab}
                                onSelect={(k) => setActiveTab(k)}
                                className="px-4 mb-3 custom-tabs flex-shrink-0 pt-3"
                                justify
                            >
                                <Tab eventKey="video" title="Video" className="flex-grow-1 h-100 overflow-hidden">
                                    {renderDownloadList(videoFormats, 'video')}
                                </Tab>
                                <Tab eventKey="no-audio" title="No Audio" className="flex-grow-1 h-100 overflow-hidden">
                                    {renderDownloadList(videoNoAudioFormats, 'no-audio')}
                                </Tab>
                                <Tab eventKey="audio" title="Audio" className="flex-grow-1 h-100 overflow-hidden">
                                    {renderDownloadList(audioFormats, 'audio')}
                                </Tab>
                            </Tabs>
                        ) : (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-2 text-muted">Loading formats...</p>
                            </div>
                        )}
                    </Card.Body>
                 </Card>
            </Col>
        </Row>
    </div>
  );
};

export default DownloadContainer;
